import { useCallback, useContext, useMemo, useRef, useState } from "react";
import {
  API_PATHS,
  INTERACTION_COLORS,
  BORDERS,
  ANIMATION_CONFIG,
  PLANNING_TOOLTIP_MESSAGES,
} from "../../../../config";

import { WeeklyContext } from "../../../../context/global/WeeklyContext";
import { DispatchContext } from "../../../../context/domain/planning/DispatchContext";
import { CaptureContext } from "../../../../context/domain/planning/CaptureContext";

import LinkifiedTextArea from "../../../../components/common/LinkifiedTextArea";
import SelectScopeModal from "../SelectScopeModal";
import ScopeWorkersSection from "./ScopeWorkersSection";
import { ConfirmModal } from "../../../../components/ui";
import { ScopeDetailsSection } from "./ScopeDetailsSection";

import {
  formatDate,
  getDateInLocalTZFromDateStr,
  getDateString,
  timeToString,
} from "../../../../utils/datetime";
import { debounce } from "../../../../utils/common";
import { getAddresses, updateWorkScopeAspect } from "../../../../services/api/dispatch";
import {
  patchPlanningScopeBlock,
  updateScope as updatePlanningScopeAspect,
} from "../../../../services/api/planning";
import { apiRequest } from "../../../../services/api/axios";

import { DISPATCH_DAY_LOCKED_UI } from "../constants";
import SkippedDayOverlay from "../../shared/SkippedDayOverlay";
import { getScopeBlockHighlightHex } from "../workScopeSummary/helpers";

import { GoPencil } from "react-icons/go";
import { IoEllipsisHorizontal } from "react-icons/io5";

/**
 * Return a nested entity id, preserving primitive ids from API write payloads.
 *
 * @param {Object|number|string|null|undefined} value - Nested object or primitive id.
 * @returns {number|string|null}
 */
function getEntityId(value) {
  if (value == null || value === "") {
    return null;
  }
  return typeof value === "object" ? value.id ?? null : value;
}

/**
 * Copy JSON-backed extra data sections without sharing object references.
 *
 * @param {Array<Object>} sections - Dispatch extra data sections.
 * @returns {Array<Object>}
 */
function cloneExtraDataSections(sections) {
  return (sections ?? []).map((section) => ({ ...section }));
}

/**
 * Build write payload rows for concrete assignments from the dispatch read shape.
 *
 * @param {Array<Object>} assignments - Serialized concrete assignment rows.
 * @returns {Array<Object>}
 */
function copyConcreteAssignments(assignments) {
  return (assignments ?? [])
    .map((assignment) => ({
      index: assignment.index ?? 0,
      mix: getEntityId(assignment.mix),
      time: assignment.time ?? null,
      yards: assignment.yards ?? null,
    }))
    .filter((assignment) => assignment.mix != null);
}

/**
 * Build write payload rows for equipment assignments from the dispatch read shape.
 *
 * @param {Array<Object>} assignments - Serialized equipment assignment rows.
 * @returns {Array<Object>}
 */
function copyEquipmentAssignments(assignments) {
  return (assignments ?? [])
    .map((assignment) => ({
      index: assignment.index ?? 0,
      asset_id: getEntityId(assignment.asset),
      rent: Boolean(assignment.rent),
    }))
    .filter((assignment) => assignment.asset_id != null);
}

/**
 * Build write payload rows for subcontractor assignments from the dispatch read shape.
 *
 * @param {Array<Object>} assignments - Serialized subcontractor assignment rows.
 * @returns {Array<Object>}
 */
function copySubcontractorAssignments(assignments) {
  return (assignments ?? [])
    .map((assignment) => ({
      index: assignment.index ?? 0,
      contractor: getEntityId(assignment.contractor),
      hours: assignment.hours ?? null,
    }))
    .filter((assignment) => assignment.contractor != null);
}

/**
 * Build write payload rows for dumping assignments from the dispatch read shape.
 *
 * @param {Array<Object>} assignments - Serialized dumping assignment rows.
 * @returns {Array<Object>}
 */
function copyDumpingAssignments(assignments) {
  return (assignments ?? [])
    .map((assignment) => ({
      index: assignment.index ?? 0,
      contractor: getEntityId(assignment.contractor),
      quantity: assignment.quantity ?? null,
    }))
    .filter((assignment) => assignment.contractor != null);
}

/**
 * Component for the work scope box in the Daily Dispatch app.
 *
 * @NOTE This is really badly coded and inefficient with strange logic and state
 * management, prop drilling, and hardcoded data. It needs to be refactored.
 *
 * Coded by Ben.
 *
 * @comment Aaron: Somehow this works, so I am not refactoring it for now as we are
 * massively behind schedule. But it is really bad code and needs simplifying, I am sure
 * it will break at some point.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {number} props.index - Scope index within the day.
 * @param {Date} props.date - Calendar date for this scope card.
 * @param {Object} props.scope - WorkScopeAspect row rendered in details view.
 * @param {boolean} props.nullScope - Whether this row is an empty placeholder.
 * @param {{ id: number|string, colorHex: string }|null} [props.hoveredBlock] - Hovered Schedule block shared across the job row.
 * @param {Function} [props.onBlockHover] - Setter for the hovered Schedule block.
 * @returns {JSX.Element}
 */
export function EditScopeCard({
  index,
  date,
  scope,
  nullScope,
  hoveredBlock = null,
  onBlockHover = null,
}) {
  const {
    updateScope,
    copyScopeOrigin,
    copyScopesDate,
    scopesToDelete,
    isReadOnly,
    runPlanningOperation,
    openScopeActionMenu,
    dayDetails,
  } = useContext(DispatchContext);
  const { reloadScopes, applyScopePatch } = useContext(WeeklyContext);
  const captureContext = useContext(CaptureContext);
  // We need to get captureComponent like this as captureContext is not always available
  var captureComponent = false;
  if (captureContext) {
    captureComponent = captureContext.captureComponent;
  }

  const [notes, setNotes] = useState(scope.notes || "");
  const [extraDataSections, setExtraDataSections] = useState(
    JSON.parse(JSON.stringify(scope.extra_data_sections ?? []))
  );
  const extraDataSectionsRef = useRef(extraDataSections);
  extraDataSectionsRef.current = extraDataSections;

  const [isEditScopeModalOpen, setIsEditScopeModalOpen] = useState(false);
  const [pendingBlockScopeChange, setPendingBlockScopeChange] = useState(null);
  const [pendingExtraDataSectionDeleteIndex, setPendingExtraDataSectionDeleteIndex] =
    useState(null);
  const copyScopesDateMatch = getDateString(copyScopesDate) === scope.date;

  /** True when Dispatch DayDetails for this scope's calendar day has locked set on the server. */
  const isDayLocked = useMemo(() => {
    if (!dayDetails?.length) {
      return false;
    }
    return Boolean(
      dayDetails.find(
        (d) =>
          d.date ===
          formatDate(getDateInLocalTZFromDateStr(scope.date), "YYYY-MM-DD")
      )?.locked
    );
  }, [dayDetails, scope.date]);

  /** Open the shared Dispatch scope action menu from the edit-card ellipsis button. */
  const handleOpenScopeActionMenu = useCallback(
    (event) => {
      const rect = event.currentTarget.getBoundingClientRect();
      openScopeActionMenu({
        scope,
        date,
        anchorRect: {
          top: rect.top,
          right: rect.right,
        },
      });
    },
    [date, openScopeActionMenu, scope]
  );

  // NOTE: Below this line has already been refactored (out of necessity)
  const persistWorkScopeAspectChange = useCallback(
    async ({ scopeObject, quantity, forceBlockChange = false }) => {
      const scheduleBlock = scope.schedule_block ?? null;
      const scheduleBlockId = scheduleBlock?.id ?? null;
      const blockAspectCount = Number(scheduleBlock?.aspect_count ?? 0);
      const scopeChanged =
        scopeObject?.id != null && String(scopeObject.id) !== String(scope.scope?.id);
      const quantityChanged = String(quantity ?? "") !== String(scope.quantity ?? "");
      const shouldPatchBlock = Boolean(scheduleBlockId && scopeChanged);
      const shouldConfirmBlockChange =
        shouldPatchBlock && blockAspectCount > 1 && !forceBlockChange;

      if (shouldConfirmBlockChange) {
        setPendingBlockScopeChange({ scopeObject, quantity });
        setIsEditScopeModalOpen(false);
        return;
      }

      const resources = shouldPatchBlock
        ? ["dispatch.aspects", "schedule.blocks", "schedule.aspects"]
        : ["dispatch.aspects"];
      const entityIds = shouldPatchBlock
        ? [scope.id, scheduleBlockId]
        : [scope.id];

      try {
        await runPlanningOperation({
          module: "dispatch",
          operation: "updateDispatchScope",
          resources,
          entityIds,
          apiCall: async () => {
            if (shouldPatchBlock) {
              await patchPlanningScopeBlock(scheduleBlockId, {
                work_scope_id: scopeObject.id,
              });
            }
            if (!shouldPatchBlock || quantityChanged) {
              const payload = shouldPatchBlock
                ? { quantity }
                : { scope: scopeObject, quantity };
              return updateWorkScopeAspect(scope.id, payload);
            }
            return null;
          },
        });
      } catch (e) {
        console.error(
          `could not update work scope aspect on scope id ${scope.id}`,
          e
        );
      }
      setIsEditScopeModalOpen(false);
      setPendingBlockScopeChange(null);
      reloadScopes();
    },
    [
      scope.id,
      scope.quantity,
      scope.schedule_block,
      scope.scope?.id,
      reloadScopes,
      runPlanningOperation,
    ]
  );

  const handleEditWorkScopeAspect = useCallback(
    (scopeObject, quantity) => {
      void persistWorkScopeAspectChange({ scopeObject, quantity });
    },
    [persistWorkScopeAspectChange]
  );

  const handleConfirmBlockScopeChange = useCallback(() => {
    if (!pendingBlockScopeChange) {
      return;
    }
    void persistWorkScopeAspectChange({
      ...pendingBlockScopeChange,
      forceBlockChange: true,
    });
  }, [pendingBlockScopeChange, persistWorkScopeAspectChange]);

  /** Create a one-day block for the pending scope change, then skip the original aspect. */
  const handleChangeScopeForDayOnly = useCallback(async () => {
    if (
      !pendingBlockScopeChange?.scopeObject?.id ||
      !scope.schedule_block?.id ||
      !scope.job?.id
    ) {
      return;
    }

    const { scopeObject, quantity } = pendingBlockScopeChange;
    const scheduleBlockId = scope.schedule_block.id;
    const workerIds = (scope.worker_assignment ?? [])
      .map((worker) => worker.id)
      .filter((id) => id != null && id !== "");
    const normalizedQuantity = quantity === "" ? 0 : quantity;
    let didPersist = false;

    try {
      await runPlanningOperation({
        module: "dispatch",
        operation: "changeDispatchScopeForDayOnly",
        resources: ["dispatch.aspects", "schedule.blocks", "schedule.aspects"],
        entityIds: [scope.id, scheduleBlockId],
        apiCall: async () => {
          const sourceBlock = await apiRequest(
            `${API_PATHS.SCHEDULE.BLOCKS}${scheduleBlockId}/`,
            null,
            { method: "GET" }
          );
          const createdBlock = await apiRequest(
            API_PATHS.SCHEDULE.BLOCKS,
            {
              job_id: scope.job.id,
              work_scope_id: scopeObject.id,
              schedule_group_id: sourceBlock?.schedule_group?.id ?? null,
              start_date: scope.date,
              end_date: scope.date,
              working_days_only: false,
              worker_ids: workerIds,
              merge_adjacent_blocks: true,
            },
            { method: "POST" }
          );
          const createdAspect =
            (createdBlock?.aspects ?? []).find((aspect) => aspect.date === scope.date) ??
            createdBlock?.aspects?.[0];

          if (!createdAspect?.id) {
            throw new Error("Schedule block create did not return a day aspect.");
          }

          const aspectPatch = {
            quantity: normalizedQuantity,
            skipped: false,
            skipped_reason: scope.skipped_reason ?? "",
            notes: notes ?? "",
            extra_data_sections: cloneExtraDataSections(extraDataSectionsRef.current),
            worker_assignment: workerIds,
            concrete: copyConcreteAssignments(scope.concrete),
            equipment: copyEquipmentAssignments(scope.equipment),
            subcontractor: copySubcontractorAssignments(scope.subcontractor),
            dumping: copyDumpingAssignments(scope.dumping),
          };

          if (scope.custom_time) {
            aspectPatch.custom_time = scope.custom_time;
          }

          if (scope.custom_address) {
            try {
              const addresses = await getAddresses();
              const matchingAddress = (addresses ?? []).find(
                (address) => address.address === scope.custom_address
              );
              if (matchingAddress?.id) {
                aspectPatch.custom_address = matchingAddress.id;
              }
            } catch (addressError) {
              console.error("Could not resolve custom address for copied scope", addressError);
            }
          }

          await updatePlanningScopeAspect(createdAspect.id, aspectPatch, "dispatch");
          await updatePlanningScopeAspect(scope.id, { skipped: true }, "dispatch");
          return createdBlock;
        },
      });
      didPersist = true;
      applyScopePatch(scope.id, { skipped: true });
    } catch (e) {
      console.error(`could not change scope for day only on scope id ${scope.id}`, e);
    } finally {
      setPendingBlockScopeChange(null);
      if (didPersist) {
        reloadScopes();
      }
    }
  }, [
    applyScopePatch,
    pendingBlockScopeChange,
    reloadScopes,
    runPlanningOperation,
    scope,
    notes,
  ]);

  // Main scope notes field (WorkScopeAspects.notes)
  const handleEditNotes = useCallback(
    (e) => {
      e.preventDefault();
      const newNotes = e.currentTarget.value;
      setNotes(newNotes);
      if (scope.id) {
        debounce((newNotes) => {
          updateScope(scope.id, { notes: newNotes }).catch((e) => {
            console.error(`could not update newNotes on scope id ${scope.id}`, e);
          });
        }, 100)(newNotes);
      }
    },
    [updateScope, scope, setNotes]
  );

  const handleNotesBlur = useCallback(() => {
    if (scope.id) {
      updateScope(scope.id, { notes }).catch((e) => {
        console.error(`could not update notes on scope id ${scope.id}`, e);
      });
    }
  }, [notes, scope.id, updateScope]);

  // Extra DATA sections section
  const handleAddExtraDataSection = useCallback(() => {
    const nextSections = [
      { title: "Extra Notes", body: "" },
      ...extraDataSectionsRef.current,
    ];
    extraDataSectionsRef.current = nextSections;
    setExtraDataSections(nextSections);
    if (scope.id) {
      updateScope(scope.id, { extra_data_sections: nextSections }).catch((err) => {
        console.error(
          `could not update extra_data_sections on scope id ${scope.id}`,
          err
        );
      });
    }
  }, [scope.id, updateScope]);

  const performDeleteExtraDataSection = useCallback(
    (index) => {
      const nextSections = extraDataSectionsRef.current.filter((_, i) => i !== index);
      extraDataSectionsRef.current = nextSections;
      setExtraDataSections(nextSections);

      if (scope.id) {
        updateScope(scope.id, { extra_data_sections: nextSections }).catch((err) => {
          console.error(
            `could not update extra_data_sections on scope id ${scope.id}`,
            err
          );
        });
      }
    },
    [scope.id, updateScope]
  );

  const handleRequestDeleteExtraDataSection = useCallback((index) => {
    setPendingExtraDataSectionDeleteIndex(index);
  }, []);

  const handleConfirmDeleteExtraDataSection = useCallback(() => {
    if (pendingExtraDataSectionDeleteIndex == null) return;
    const index = pendingExtraDataSectionDeleteIndex;
    setPendingExtraDataSectionDeleteIndex(null);
    performDeleteExtraDataSection(index);
  }, [pendingExtraDataSectionDeleteIndex, performDeleteExtraDataSection]);

  const handleExtraDataSectionTitleChange = useCallback((index, newTitle) => {
    const nextSections = extraDataSectionsRef.current.map((s, i) =>
      i === index ? { ...s, title: newTitle } : s
    );
    extraDataSectionsRef.current = nextSections;
    setExtraDataSections(nextSections);
    if (scope.id) {
      debounce((nextSections) => {
        updateScope(scope.id, { extra_data_sections: nextSections }).catch((err) => {
          console.error(
            `could not update extra_data_sections on scope id ${scope.id}`,
            err
          );
        });
      }, 100)(nextSections);
    }
  }, [scope.id, updateScope]);

  const handleExtraDataSectionsBlur = useCallback(() => {
    if (scope.id) {
      updateScope(scope.id, {
        extra_data_sections: extraDataSectionsRef.current,
      }).catch((err) => {
        console.error(
          `could not update extra_data_sections on scope id ${scope.id}`,
          err
        );
      });
    }
  }, [scope.id, updateScope]);

  const handleExtraDataSectionBodyChange = useCallback(
    (index) => (e) => {
      e.preventDefault();
      const newBody = e.currentTarget.value;
      const nextSections = extraDataSectionsRef.current.map((s, i) =>
        i === index ? { ...s, body: newBody } : s
      );
      extraDataSectionsRef.current = nextSections;
      setExtraDataSections(nextSections);
      if (scope.id) {
        debounce((nextSections) => {
          updateScope(scope.id, { extra_data_sections: nextSections }).catch((err) => {
            console.error(
              `could not update extra_data_sections on scope id ${scope.id}`,
              err
            );
          });
        }, 100)(nextSections);
      }
    },
    [scope, updateScope]
  );

  const getScopeClasses = () => {
    if (scopesToDelete.includes(scope.id)) {
      return INTERACTION_COLORS.danger.bg;
    }
    if (
      copyScopesDateMatch ||
      (copyScopeOrigin && copyScopeOrigin[0] === scope.id)
    ) {
      return INTERACTION_COLORS.success.bg;
    }
    return "bg-gray-50";
  };

  const getAnimationClass = () => {
    return scopesToDelete.includes(scope.id) ? ANIMATION_CONFIG.pulse : "";
  };

  /** Report this card's Schedule block so linked details cards highlight together. */
  const handleBlockHoverStart = useCallback(() => {
    if (!onBlockHover) {
      return;
    }
    const blockId = scope.schedule_block?.id;
    if (blockId == null) {
      onBlockHover(null);
      return;
    }
    onBlockHover({ id: blockId, colorHex: getScopeBlockHighlightHex(scope) });
  }, [onBlockHover, scope]);

  /** Clear the hovered Schedule block when the pointer leaves the details card. */
  const handleBlockHoverEnd = useCallback(() => {
    onBlockHover?.(null);
  }, [onBlockHover]);

  const isBlockHighlighted =
    scope.schedule_block?.id != null &&
    hoveredBlock?.id === scope.schedule_block.id;

  return (
    <>
      {nullScope ? (
        <></>
      ) : (
        <div
          onMouseEnter={handleBlockHoverStart}
          onMouseLeave={handleBlockHoverEnd}
          className={`relative pt-[2px] ${getScopeClasses()} flex flex-col gap-y-1 mb-2 ${BORDERS.radius.sm} outline outline-1 outline-gray-300 ${getAnimationClass()} ${isDayLocked ? DISPATCH_DAY_LOCKED_UI.scopeCardMutedClass : ""}`}
          style={
            isBlockHighlighted
              ? { boxShadow: `0 0 5px 3px ${hoveredBlock.colorHex}80` }
              : undefined
          }
        >
          {scope.skipped ? (
            <SkippedDayOverlay
              className="top-[32px]"
              aspectId={scope.id}
              reason={scope.skipped_reason ?? ""}
              module="dispatch"
              onReasonChange={(nextReason) =>
                applyScopePatch(scope.id, { skipped_reason: nextReason })
              }
            />
          ) : null}
          <div className={"flex flex-col"}>
            <div
              className={
                "mx-2 mb-0 grid grid-cols-[76%_auto_auto] grid-rows-[auto_auto] gap-0 text-gray-800"
              }
            >
              {scope.scope ? (
                <>
                  <div
                    className={
                      "flex flex-row font-semibold items-center min-h-[24px] space-x-1 col-span-1 row-span-1"
                    }
                  >
                    <p className="pr-2 leading-tight min-h-[24px] pt-[2px]">
                      {scope.scope.title}
                    </p>
                  </div>
                  <div
                    className={"text-gray-600 text-sm col-span-1 row-span-2"}
                  >
                    {!isReadOnly && (
                      <button
                        className={"text-[0.9rem] mt-1"}
                        onClick={() => setIsEditScopeModalOpen(true)}
                      >
                        <GoPencil />
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex justify-center items-center col-span-2 row-span-1">
                  <button
                    className={
                      "font-medium rounded w-full max-w-xs outline outline-gray-300 outline-1 bg-blue-50 hover:bg-blue-100"
                    }
                    onClick={() => setIsEditScopeModalOpen(true)}
                  >
                    Select Scope
                  </button>
                </div>
              )}
              <div className={"col-span-1 row-span-1 flex justify-end"}>
                <div className="text-base">
                  {!isReadOnly && (
                    <button
                      className="my-auto align-middle"
                      onClick={handleOpenScopeActionMenu}
                    >
                      <IoEllipsisHorizontal />
                    </button>
                  )}
                </div>
              </div>
              <>
                {scope.quantity ? (
                  <div className="text-gray-600 text-sm col-span-1 row-span-1 max-h-6">
                    {scope.quantity} {scope.scope?.unit?.name || "Qty"}
                  </div>
                ) : null}
              </>
            </div>

            {/*Custom values section*/}
            {scope["custom_address"] && (
              <div className={"text-gray-600 text-sm mx-2"}>
                {scope["custom_address"]}
              </div>
            )}
            {scope["custom_time"] && (
              <div className={"text-gray-600 text-sm mx-2"}>
                {timeToString(scope["custom_time"])}
              </div>
            )}
          </div>

          {/*Workers section*/}
          <ScopeWorkersSection scope={scope} />

          {/*Details section*/}
          <ScopeDetailsSection scope={scope} />

          {/* Main notes + extra data sections */}
          {captureComponent ? (
            <>
              <LinkifiedTextArea
                staticDisplay
                label="Notes"
                value={notes}
                placeholder="Add notes"
                className="flex-grow bg-transparent w-full caret-black pt-1 px-2 overflow-y-auto min-h-[88px] h-full resize-y"
              />
              {extraDataSections.map((sec, index) => (
                <LinkifiedTextArea
                  key={index}
                  staticDisplay
                  label={sec.title || "Section"}
                  value={sec.body}
                  placeholder="Add content"
                  className="flex-grow bg-transparent w-full caret-black pt-1 px-2 overflow-y-auto min-h-[88px] h-full resize-y"
                />
              ))}
            </>
          ) : (
            <>
              <LinkifiedTextArea
                label="Notes"
                placeholder="Add notes"
                value={notes}
                onChange={handleEditNotes}
                onBlur={handleNotesBlur}
                readOnly={isReadOnly}
                onAdd={handleAddExtraDataSection}
                className="flex-grow bg-transparent w-full caret-black pt-1 px-2 overflow-y-auto min-h-[88px] h-full resize-y"
              />
              {extraDataSections.map((sec, index) => (
                <LinkifiedTextArea
                  key={index}
                  label={sec.title || "Section"}
                  readOnly={isReadOnly}
                  placeholder="Add content"
                  value={sec.body}
                  onChange={handleExtraDataSectionBodyChange(index)}
                  onLabelChange={(newTitle) =>
                    handleExtraDataSectionTitleChange(index, newTitle)
                  }
                  onBlur={handleExtraDataSectionsBlur}
                  onLabelBlur={handleExtraDataSectionsBlur}
                  onDelete={() => handleRequestDeleteExtraDataSection(index)}
                  className="flex-grow bg-transparent w-full caret-black pt-1 px-2 overflow-y-auto min-h-[88px] h-full resize-y"
                />
              ))}
            </>
          )}
          {isDayLocked ? (
            <div
              className="absolute inset-0 z-[5] "
              title={PLANNING_TOOLTIP_MESSAGES.editingDisabledWhenLocked}
              aria-label={PLANNING_TOOLTIP_MESSAGES.editingDisabledWhenLocked}
            />
          ) : null}
        </div>
      )}
      <SelectScopeModal
        isOpen={isEditScopeModalOpen}
        onClose={() => setIsEditScopeModalOpen(false)}
        onConfirm={handleEditWorkScopeAspect}
        job={scope.job}
        date={date}
        initialScope={scope.scope}
        initialQuantity={scope.quantity}
      />
      <ConfirmModal
        isOpen={pendingBlockScopeChange != null}
        title="Change scope?"
        textAlign="left"
        textHTML="<b><i>This scope is part of a larger scope block, would you like to..?</i></b><ul><li><b>Change for Day Only</b> - Split this scope from the block, set this day as skipped in the block, and update the scope for this day only.</li><li><b>Change for Block</b> - Update the scope for all days in this block</li></ul>"
        buttonText="Change for Block"
        secondaryButtonText="Change for Day Only"
        onConfirm={handleConfirmBlockScopeChange}
        onSecondaryConfirm={handleChangeScopeForDayOnly}
        onClose={() => setPendingBlockScopeChange(null)}
      />
      <ConfirmModal
        isOpen={pendingExtraDataSectionDeleteIndex != null}
        title={"Remove extra data section?"}
        list={
          pendingExtraDataSectionDeleteIndex != null
            ? [
                extraDataSections[pendingExtraDataSectionDeleteIndex]?.title?.trim() ||
                  "Section",
              ]
            : []
        }
        text={
          "Are you sure? This action cannot be undone."
        }
        buttonText={"Remove"}
        onConfirm={handleConfirmDeleteExtraDataSection}
        onClose={() => setPendingExtraDataSectionDeleteIndex(null)}
      />
    </>
  );
}
